import React, { createContext, useState, useEffect } from 'react';
import { AccessibilityContextType, AccessibilityProviderProps, ACCESSIBILITY_CONSTANTS } from './accessibility-types';
import { setAccessibilityContext } from './use-accessibility';

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

// Configurar o context para o hook
setAccessibilityContext(AccessibilityContext);

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({ children }) => {
  const [highContrast, setHighContrast] = useState(() => {
    return localStorage.getItem(ACCESSIBILITY_CONSTANTS.STORAGE_KEYS.HIGH_CONTRAST) === 'true';
  });
  
  const [fontSize, setFontSize] = useState(() => {
    return parseInt(localStorage.getItem(ACCESSIBILITY_CONSTANTS.STORAGE_KEYS.FONT_SIZE) || ACCESSIBILITY_CONSTANTS.DEFAULT_FONT_SIZE.toString());
  });
  
  const [reducedMotion, setReducedMotion] = useState(() => {
    return localStorage.getItem(ACCESSIBILITY_CONSTANTS.STORAGE_KEYS.REDUCED_MOTION) === 'true';
  });
  
  const [focusVisible, setFocusVisible] = useState(false);
  const [screenReader, setScreenReader] = useState(false);
  const [keyboardNavigation, setKeyboardNavigation] = useState(false);

  // Aplicar configurações ao DOM
  useEffect(() => {
    const root = document.documentElement;
    
    // Alto contraste
    if (highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    // Tamanho da fonte
    root.style.fontSize = `${fontSize}%`;
    
    // Movimento reduzido
    if (reducedMotion) {
      root.classList.add('reduced-motion');
    } else {
      root.classList.remove('reduced-motion');
    }
    
    // Salvar no localStorage
    localStorage.setItem(ACCESSIBILITY_CONSTANTS.STORAGE_KEYS.HIGH_CONTRAST, highContrast.toString());
    localStorage.setItem(ACCESSIBILITY_CONSTANTS.STORAGE_KEYS.FONT_SIZE, fontSize.toString());
    localStorage.setItem(ACCESSIBILITY_CONSTANTS.STORAGE_KEYS.REDUCED_MOTION, reducedMotion.toString());
  }, [highContrast, fontSize, reducedMotion]);

  // Detectar navegação por teclado
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        setKeyboardNavigation(true);
        setFocusVisible(true);
      }
    };

    const handleMouseDown = () => {
      setKeyboardNavigation(false);
    };

    const handleFocusVisible = () => {
      if (keyboardNavigation) {
        setFocusVisible(true);
      }
    };

    const handleBlur = () => {
      setFocusVisible(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('focusin', handleFocusVisible);
    document.addEventListener('focusout', handleBlur);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('focusin', handleFocusVisible);
      document.removeEventListener('focusout', handleBlur);
    };
  }, [keyboardNavigation]);

  const toggleHighContrast = () => {
    setHighContrast(prev => !prev);
  };

  const increaseFontSize = () => {
    setFontSize(prev => Math.min(prev + ACCESSIBILITY_CONSTANTS.FONT_SIZE_STEP, ACCESSIBILITY_CONSTANTS.MAX_FONT_SIZE));
  };

  const decreaseFontSize = () => {
    setFontSize(prev => Math.max(prev - ACCESSIBILITY_CONSTANTS.FONT_SIZE_STEP, ACCESSIBILITY_CONSTANTS.MIN_FONT_SIZE));
  };

  const resetFontSize = () => {
    setFontSize(ACCESSIBILITY_CONSTANTS.DEFAULT_FONT_SIZE);
  };

  const toggleReducedMotion = () => {
    setReducedMotion(prev => !prev);
  };

  const toggleScreenReader = () => {
    setScreenReader(prev => !prev);
  };

  const value: AccessibilityContextType = {
    highContrast,
    toggleHighContrast,
    fontSize,
    increaseFontSize,
    decreaseFontSize,
    resetFontSize,
    reducedMotion,
    toggleReducedMotion,
    focusVisible,
    setFocusVisible,
    screenReader,
    toggleScreenReader,
    keyboardNavigation,
    setKeyboardNavigation,
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
}; 